import z from "zod"
import { GeneratedRequirementSchema } from "../composer/requirements"
import { buildPaginationSchema } from "../shared/pagination_dtos"
import { WebhookCapabilityConfigBaseSchema, WebhookStepSchema } from "./config"

export const CapabilityIdentifier = {
  FindOrderNumber: "FindOrderNumber",
  GetOrderTracking: "GetOrderTracking",
  GetOrderRefundStatus: "GetOrderRefundStatus",
  CancelOrder: "CancelOrder",
  SearchCatalog: "SearchCatalog",
  ProductInformation: "ProductInformation",
  Webhook: "Webhook",
  Instruction: "Instruction",
  MCP: "MCP",
  BusinessOpportunity: "BusinessOpportunity",
  ReactToMessage: "ReactToMessage",
  Support: "Support",
  RequestReview: "RequestReview",
  UpdateOrder: "UpdateOrder",
} as const

export type CapabilityIdentifier = (typeof CapabilityIdentifier)[keyof typeof CapabilityIdentifier]

export const CapabilityKind = {
  Integration: "Integration",
  Webhook: "Webhook",
  Instruction: "Instruction",
  MCP: "MCP",
  Native: "Native",
} as const

export type CapabilityKind = (typeof CapabilityKind)[keyof typeof CapabilityKind]

export const CapabilityExecutionStatus = {
  Success: "Success",
  Unsuccess: "Unsuccess",
  Cached: "Cached",
  Error: "Error",
  Skipped: "Skipped",
} as const

export type CapabilityExecutionStatus =
  (typeof CapabilityExecutionStatus)[keyof typeof CapabilityExecutionStatus]

export const CapabilityCampaignAssignation = {
  AllCampaigns: "AllCampaigns",
  HandPicked: "HandPicked",
  CampaignFlow: "CampaignFlow",
} as const

export type CapabilityCampaignAssignation =
  (typeof CapabilityCampaignAssignation)[keyof typeof CapabilityCampaignAssignation]

export const GetCapabilityByIdSchema = z.object({
  id: z.string(),
})

const capabilityExecutionsFilterFields = {
  campaignId: z
    .string()
    .optional()
    .transform((val) => (val ? BigInt(val) : undefined)),
  status: z.nativeEnum(CapabilityExecutionStatus).optional(),
  // Inline to avoid circular imports with conversations/response_data
  conversationType: z
    .enum({
      Live: "Live",
      Testing: "Testing",
    } as const)
    .optional(),
  rangeDays: z.coerce
    .number()
    .pipe(z.union([z.literal(7), z.literal(30)]))
    .optional()
    .default(7),
}

export const GetCapabilityExecutionsSchema = buildPaginationSchema().extend(
  capabilityExecutionsFilterFields,
)

export const GetCapabilityExecutionsAnalyticsSchema = z.object(capabilityExecutionsFilterFields)

const capabilityActivationFields = {
  campaignAssignation: z.enum(CapabilityCampaignAssignation),
  campaignIds: z.array(z.string()).optional(),
  // Inline enum instead of importing ConversationFlowEnum to avoid a circular reference
  // (conversations/response_data → capabilities → conversations/response_data)
  campaignFlow: z
    .enum({
      Inbound: "Inbound",
      Outbound: "Outbound",
    } as const)
    .optional(),
}

const campaignFlowRefinement = (data: { campaignAssignation: string; campaignFlow?: string }) =>
  data.campaignAssignation !== "CampaignFlow" || data.campaignFlow !== undefined

const campaignFlowRefinementMessage = {
  message: "campaignFlow is required when campaignAssignation is CampaignFlow",
  path: ["campaignFlow"],
}

export const UpdateCapabilityActivationBodySchema = z
  .object(capabilityActivationFields)
  .refine(campaignFlowRefinement, campaignFlowRefinementMessage)

export const UpdateCapabilityConfigBodySchema = z.object({
  config: z.record(z.string(), z.any()),
  requirements: z.array(GeneratedRequirementSchema).optional(),
})

export const InstallCapabilityBodySchema = z
  .object({
    identifier: z.enum(CapabilityIdentifier),
    config: z.record(z.string(), z.any()),
    requirements: z.array(GeneratedRequirementSchema).optional(),
  })
  .extend(capabilityActivationFields)
  .refine(campaignFlowRefinement, campaignFlowRefinementMessage)

export const UninstallCapabilityBodySchema = z.object({
  id: z.string(),
})

export const ExtractCapabilityRequirementsBodySchema = z.object({
  capabilityName: z.string().trim().min(1).max(255),
  capabilityKind: z.enum(CapabilityKind),
  capabilityContext: z.string().trim().min(1).max(10000),
  agentInstructions: z.string().trim().min(1).max(10000),
})

export const TestWebhookBodySchema = WebhookCapabilityConfigBaseSchema.pick({
  url: true,
  webhookHeaders: true,
  webhookBody: true,
  webhookBodyFormat: true,
  method: true,
})
  .required({ url: true })
  .extend({
    url: z.string().min(1),
    variables: z.record(z.string(), z.string()).default({}),
  })

export const TestWebhookChainStepBodySchema = z.object({
  step: WebhookStepSchema.pick({
    url: true,
    method: true,
    webhookHeaders: true,
    webhookBody: true,
    webhookBodyFormat: true,
  }),
  priorResponses: z.array(z.string()).default([]),
  variables: z.record(z.string(), z.string()).default({}),
})

export const TestWebhookResponseSchema = z.object({
  valid: z.boolean(),
  errors: z.array(z.object({ field: z.string(), message: z.string() })).optional(),
})

export const TestMcpBodySchema = z.object({
  transportType: z.enum(["sse", "streamable-http"]).default("streamable-http"),
  serverUrl: z.string().url(),
  authConfig: z
    .object({
      type: z.enum(["none", "bearer", "apiKey", "custom"]),
      token: z.string().optional(),
      apiKey: z.string().optional(),
      headerName: z.string().optional(),
      customHeaders: z.record(z.string(), z.string()).optional(),
    })
    .optional(),
  connectionTimeoutMs: z.coerce.number().positive().default(10000),
})

export const UpsertCampaignCapabilityConfigBodySchema = z.object({
  campaignId: z.string(),
  configOverride: z.record(z.string(), z.unknown()),
})

export const DeleteCampaignCapabilityConfigBodySchema = z.object({
  campaignId: z.string(),
})

export const GetCapabilitiesQuerySchema = z.object({
  campaignId: z.string().optional(),
})

export const TestMcpResponseSchema = z.object({
  success: z.boolean(),
  toolsDiscovered: z.number(),
  tools: z.array(
    z.object({
      name: z.string(),
      description: z.string().optional(),
      inputSchema: z.record(z.string(), z.unknown()).optional(),
    }),
  ),
  errors: z.array(z.object({ field: z.string(), message: z.string() })).optional(),
})
